import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  TableCell,
  TableRow,
  TableBody,
  Typography,
  Table,
  TableHead,
  TableSortLabel,
  TablePagination,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { MdOutlineDelete } from "react-icons/md";
import Controls from "../../../components/controls/Controls";
import ConfirmDialog from "../../../components/customHelpers/ConfirmDialog";
import Notify from "../../../components/customHelpers/Notify";
import Notification from "../../../components/customHelpers/Notification";
import { formatNumber } from "../../../utils/utils";

const useStyles = makeStyles(() => ({
  table: {
    marginTop: "8px",
    width: "100%",
    "& thead th": {
      fontWeight: 600,
      color: "rgb(20,61,89)",
      backgroundColor: "rgba(244,151,3,0.8)",
      userSelect: "text",
      pointerEvents: "auto",
      padding: "8px",
      fontSize: "0.9rem",
      lineHeight: "1.2rem",
    },
    "& tbody td": {
      fontWeight: 300,
      padding: "2px",
      userSelect: "text",
      pointerEvents: "auto",
    },
    "& tbody tr:hover": {
      backgroundColor: "#fffbf2",
      cursor: "pointer",
    },
  },
}));

const headCells = [
  { id: "productName", label: "Product" },
  { id: "store", label: "Store" },
  { id: "sku", label: "Sku" },
  { id: "quantity", label: "Quantity" },
  { id: "price", label: "Price" },
  { id: "discount", label: "Discount" },
  { id: "totalPrice", label: "TotalPrice" },
  { id: "actions", label: "Actions", disableSorting: true },
];

const OrderTable = ({
  productLists,
  setData,
  paid,
  setPaid,
  setProducts,
  products,
  removePendingOrderLocally,
}) => {
  const classes = useStyles();
  const { NotifyMessage, notify, setNotify } = Notify();

  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });

  useEffect(() => {
    setOrders(productLists);
  }, [productLists]);

  const handleSortRequest = (cellId) => {
    const isAsc = orderBy === cellId && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(cellId);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const orderResult = comparator(a[0], b[0]);
      return orderResult !== 0 ? orderResult : a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const descendingComparator = (a, b, orderBy) => {
    return a[orderBy] < b[orderBy] ? -1 : a[orderBy] > b[orderBy] ? 1 : 0;
  };

  const getComparator = (order, orderBy) =>
    order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => descendingComparator(b, a, orderBy);

  const recordsAfterPagingAndSorting = () => {
    return stableSort(orders, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      (page + 1) * rowsPerPage
    );
  };

  const removeOrderItem = (item, quantity) => {
    setOrders((prevOrders) => {
      return prevOrders.filter(
        (order) =>
          !(
            order.product === item.product &&
            Number(order.price.trim()) === Number(item.price.trim()) &&
            Number(order.discount) === Number(item.discount)
          )
      );
    });

    setData((prevOrders) => {
      return prevOrders.filter(
        (order) =>
          !(
            order.product === item.product &&
            Number(order.price.trim()) === Number(item.price.trim()) &&
            Number(order.discount) === Number(item.discount)
          )
      );
    });

    setPaid(paid - item.totalPrice);

    const updatedProducts = products.map((product) =>
      product.id === item.product
        ? { ...product, quantity: product.quantity + quantity }
        : product
    );
    setProducts(updatedProducts);

    removePendingOrderLocally(item.sku, quantity);
  };

  const onDelete = (item, quantity) => {
    setConfirmDialog({ ...confirmDialog, isOpen: false });
    removeOrderItem(item, quantity);
  };

  return (
    <div className="card">
      <Grid container>
        <Grid item xs={12} className="card__body">
          <div style={{ overflowX: "auto" }}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      sortDirection={orderBy === headCell.id ? order : false}
                    >
                      {headCell.disableSorting ? (
                        headCell.label
                      ) : (
                        <TableSortLabel
                          active={orderBy === headCell.id}
                          direction={orderBy === headCell.id ? order : "asc"}
                          onClick={() => handleSortRequest(headCell.id)}
                        >
                          {headCell.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {recordsAfterPagingAndSorting().map((item) => (
                  <TableRow key={item.sku} hover>
                    <TableCell>{item.productName}</TableCell>
                    <TableCell>{item.store}</TableCell>
                    <TableCell>{item.sku}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{item.price}</TableCell>
                    <TableCell>{item.discount ?? 0}</TableCell>
                    <TableCell>{formatNumber(item.totalPrice)}</TableCell>
                    <TableCell>
                      <Controls.ActionButton
                        color="secondary"
                        title="Delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDialog({
                            isOpen: true,
                            title: "Are you sure to remove this Order?",
                            subTitle: "You can't undo this operation",
                            onConfirm: () => onDelete(item, item.quantity),
                          });
                        }}
                      >
                        <MdOutlineDelete fontSize="medium" />
                      </Controls.ActionButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <TablePagination
            component="div"
            page={page}
            rowsPerPageOptions={[15, 25, 50, 75, 100]}
            rowsPerPage={rowsPerPage}
            count={orders.length}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Grid>
      </Grid>
      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
      <Notification notify={notify} setNotify={setNotify} />
    </div>
  );
};

export default OrderTable;
